import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { api, timeAgo, type Invite, type Member, type Role } from "../api.ts";
import { Select } from "../components/Select.tsx";

const ROLES: Role[] = ["owner", "admin", "member", "viewer"];
const roleLabel = (role: Role) => role.charAt(0).toUpperCase() + role.slice(1);
const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: "Full access, including team roles and organization deletion.",
  admin: "Manage projects, media, team members, and organization settings.",
  member: "Create and manage projects and media; no team administration.",
  viewer: "View projects and download catalogs; cannot make changes.",
};
const roleOption = (role: Role) => ({
  value: role,
  label: roleLabel(role),
  description: ROLE_DESCRIPTIONS[role],
});

export function TeamPage({ currentUserId, currentRole }: { currentUserId: number; currentRole: Role }) {
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("member");
  const [inviting, setInviting] = useState(false);

  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<number | null>(null);

  const canGrantOwner = currentRole === "owner";
  const canAdmin = currentRole === "owner" || currentRole === "admin";
  const inviteRoleOptions = canGrantOwner ? ROLES : ROLES.filter((r) => r !== "owner");

  async function refresh() {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([api.listMembers(), api.listInvites()]);
      setMembers(m);
      setInvites(i);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canAdmin) refresh();
    else setLoading(false);
  }, []);

  if (!canAdmin) {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-slate-100">Team</h1>
        <p className="text-sm text-slate-400">Only organization owners and admins can manage the team.</p>
      </div>
    );
  }

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInviting(true);
    try {
      await api.inviteMember(email.trim(), inviteRole);
      setEmail("");
      setInviteRole("member");
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInviting(false);
    }
  }

  async function onRoleChange(member: Member, role: Role) {
    setError(null);
    setBusyUserId(member.userId);
    try {
      await api.updateMemberRole(member.userId, role);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyUserId(null);
    }
  }

  async function onRemove(member: Member) {
    if (!confirm(`Remove ${member.name} from this organization?`)) return;
    setError(null);
    setBusyUserId(member.userId);
    try {
      await api.removeMember(member.userId);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyUserId(null);
    }
  }

  async function onRevoke(invite: Invite) {
    setError(null);
    setBusyInviteId(invite.id);
    try {
      await api.revokeInvite(invite.id);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyInviteId(null);
    }
  }

  return (
    <div className="space-y-8">
      <section>
        <h1 className="mb-1 text-2xl font-semibold text-slate-100">Team</h1>
        <p className="text-sm text-slate-400">Manage who has access to this organization.</p>
      </section>

      <div className="team-page-content">
        <aside className="team-invite-card">
          <div>
            <h2>Invite a team member</h2>
            <p>Give someone access to this organization.</p>
          </div>
          <form onSubmit={onInvite}>
            <label>
              <span>Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
              />
            </label>
            <label>
              <span>Role</span>
              <Select
                value={inviteRole}
                onChange={(v) => setInviteRole(v as Role)}
                options={inviteRoleOptions.map(roleOption)}
                triggerClassName="team-role-trigger"
                menuClassName="role-select-menu team-role-menu"
              />
            </label>
            <button type="submit" disabled={inviting || email.trim().length === 0}>
              {inviting ? "Sending invite…" : "Send invite"}
            </button>
          </form>
        </aside>

        <div className="team-tables">
          {error && <p className="mb-4 text-sm text-rose-400">{error}</p>}
          {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Joined</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {members.map((m) => {
                  const isSelf = m.userId === currentUserId;
                  const canManage = !isSelf && (m.role !== "owner" || canGrantOwner);
                  const roleOptions = canGrantOwner ? ROLES : ROLES.filter((r) => r !== "owner");
                  return (
                    <tr key={m.userId} className="hover:bg-slate-900/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {m.avatarUrl ? (
                            <img src={m.avatarUrl} alt="" referrerPolicy="no-referrer" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-indigo-950 text-xs font-semibold text-indigo-300">
                              {m.name.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <span className="text-slate-100">
                            {m.name}
                            {isSelf && <span className="ml-1 text-slate-500">(you)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{m.email}</td>
                      <td className="px-4 py-3">
                        {canManage ? (
                          <Select
                            value={m.role}
                            disabled={busyUserId === m.userId}
                            onChange={(v) => onRoleChange(m, v as Role)}
                            options={roleOptions.map(roleOption)}
                            triggerClassName="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 focus:border-indigo-500"
                            menuClassName="role-select-menu"
                          />
                        ) : (
                          <span className="inline-block rounded-md bg-slate-800 px-2 py-0.5 text-xs capitalize text-slate-300">{m.role}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{timeAgo(m.joinedAt)}</td>
                      <td className="px-4 py-3 text-right">
                        {canManage && (
                          <button
                            onClick={() => onRemove(m)}
                            disabled={busyUserId === m.userId}
                            aria-label="Remove user"
                            data-tooltip-id="team-tip"
                            data-tooltip-content="Remove user"
                            className="rounded-md border border-slate-700 p-2 text-rose-400 hover:border-rose-500/60 hover:bg-rose-950/30 disabled:opacity-40"
                          >
                            <FaTrash size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {invites.length > 0 && (
            <section className="mt-7 space-y-3">
              <h2 className="text-sm font-medium text-slate-300">Pending invites</h2>
              <div className="overflow-hidden rounded-xl border border-slate-800">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Role</th>
                      <th className="px-4 py-3 font-medium">Invited by</th>
                      <th className="px-4 py-3 font-medium">Sent</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {invites.map((i) => (
                      <tr key={i.id} className="hover:bg-slate-900/40">
                        <td className="px-4 py-3 text-slate-100">{i.email}</td>
                        <td className="px-4 py-3">
                          <span className="inline-block rounded-md bg-slate-800 px-2 py-0.5 text-xs capitalize text-slate-300">{i.role}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-400">{i.invitedBy}</td>
                        <td className="px-4 py-3 text-slate-400">{timeAgo(i.createdAt)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => onRevoke(i)}
                            disabled={busyInviteId === i.id}
                            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-rose-400 hover:border-rose-500/60 hover:bg-rose-950/30 disabled:opacity-40"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
        </div>
      </div>
      <Tooltip
        id="team-tip"
        place="top"
        style={{backgroundColor:"#14151d",color:"#e2e2ea",padding:"6px 10px",borderRadius:7,fontSize:12,opacity:1,zIndex:50,border:"1px solid #292b38",boxShadow:"0 10px 30px rgba(0,0,0,.6)"}}
      />
    </div>
  );
}
